import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createClickNotDrag } from '../../lib/clickNotDrag';
import { isUiManagedStage, isGhlManagedRow, GHL_MANAGED_SENTENCE } from '../../lib/opportunityStages';
import {
  Plus, Filter, ChevronDown, ChevronUp, X, DollarSign,
  TrendingUp, Target, Eye, Edit2, Trash2, Save, RefreshCw, LayoutList, Columns3, CalendarDays,
  Check, AlertTriangle, Clock, Award, Sliders, Download, Upload,
  Tag, Users, UserCheck, CheckSquare, Square, Minus,
} from 'lucide-react';
import {
  safeAsync, Badge, neuShadow, neuShadowSm, neuShadowInset,
  inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg,
  overlayStyle, modalStyle, modalHeader, modalTitle,
} from './crmShared';
import CrmFilterBar from '../../components/CrmFilterBar/CrmFilterBar';
import ContactPicker from '../../components/ContactPicker';

// ─── Constants ───────────────────────────────────────────────────────────────

const OPP_STATUSES = {
  open: { label: 'Open', color: '#2E8B98', bg: '#E0F2FE' },
  won: { label: 'Won', color: '#4FA038', bg: '#DCFCE7' },
  lost: { label: 'Lost', color: '#DC3545', bg: '#FEE2E2' },
  abandoned: { label: 'Abandoned', color: '#6C757D', bg: '#F3F4F6' },
};

const DEFAULT_STAGES = ['lead', 'contacted', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];

const STAGE_LABELS = {
  lead: 'Lead', contacted: 'Contacted', qualified: 'Qualified',
  proposal: 'Proposal', negotiation: 'Negotiation',
  closed_won: 'Closed Won', closed_lost: 'Closed Lost',
  new: 'New', converted: 'Converted', lost: 'Lost', inactive: 'Inactive',
};

const STAGE_COLORS = {
  lead: '#6C757D', contacted: '#2E8B98', qualified: '#256F7A',
  proposal: '#F26522', negotiation: '#D97706', closed_won: '#4FA038', closed_lost: '#DC3545',
  new: '#6C757D', converted: '#4FA038', lost: '#DC3545', inactive: '#ADB5BD',
};

const TREATMENT_TYPES = ['NK Therapy', 'Stem Cell', 'Immunotherapy', 'Regenerative', 'Consultation', 'Other'];

const FILTER_FIELDS = [
  { value: 'title', label: 'Opportunity Name', type: 'text' },
  { value: 'value', label: 'Opportunity Value ($)', type: 'number' },
  { value: 'status', label: 'Status', type: 'select', options: ['open', 'won', 'lost', 'abandoned'] },
  { value: 'stage', label: 'Stage', type: 'select', options: DEFAULT_STAGES },
  { value: 'owner', label: 'Owner', type: 'number' },
  { value: 'probability', label: 'Probability (%)', type: 'number' },
  { value: 'contact_name', label: 'Contact Name', type: 'text' },
  { value: 'contact_email', label: 'Email', type: 'text' },
  { value: 'contact_phone', label: 'Phone', type: 'text' },
  { value: 'contact_city', label: 'City', type: 'text' },
  { value: 'contact_state', label: 'State', type: 'text' },
  { value: 'contact_source', label: 'Contact Source', type: 'text' },
  { value: 'treatment_type', label: 'Treatment Type', type: 'select', options: TREATMENT_TYPES },
  { value: 'budget', label: 'Budget', type: 'number' },
  { value: 'qualification_score', label: 'Qualification Score', type: 'number' },
  { value: 'lead_source_type', label: 'Lead Source Type', type: 'text' },
  { value: 'medical_condition', label: 'Medical Condition', type: 'text' },
  { value: 'created_at', label: 'Date Created', type: 'date' },
  { value: 'updated_at', label: 'Last Updated', type: 'date' },
  { value: 'last_status_change', label: 'Last Status Change', type: 'date' },
];

const OPERATORS_BY_TYPE = {
  text: [
    { value: 'is', label: 'is' }, { value: 'is_not', label: 'is not' },
    { value: 'contains', label: 'contains' }, { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' }, { value: 'is_not_empty', label: 'is not empty' },
  ],
  number: [
    { value: 'equals', label: 'equals' }, { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' }, { value: 'between', label: 'between' },
    { value: 'is_empty', label: 'is empty' }, { value: 'is_not_empty', label: 'is not empty' },
  ],
  date: [
    { value: 'on', label: 'on' }, { value: 'before', label: 'before' },
    { value: 'after', label: 'after' }, { value: 'between', label: 'between' },
    { value: 'is_empty', label: 'is empty' }, { value: 'is_not_empty', label: 'is not empty' },
  ],
  select: [
    { value: 'is', label: 'is' }, { value: 'is_not', label: 'is not' },
    { value: 'is_empty', label: 'is empty' }, { value: 'is_not_empty', label: 'is not empty' },
  ],
};

const DEFAULT_VISIBLE_FIELDS = ['title', 'contact_name', 'value', 'stage', 'status', 'owner_name', 'last_activity_at'];

const ALL_COLUMNS = [
  { key: 'title', label: 'Opportunity Name', sortable: true },
  { key: 'contact_name', label: 'Contact', sortable: true },
  { key: 'value', label: 'Value ($)', sortable: true, align: 'right' },
  { key: 'stage', label: 'Stage', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'probability', label: 'Probability', sortable: true, align: 'right' },
  { key: 'owner_name', label: 'Owner', sortable: true },
  { key: 'treatment_type', label: 'Treatment Type', sortable: false },
  { key: 'budget', label: 'Budget', sortable: false, align: 'right' },
  { key: 'qualification_score', label: 'Qual. Score', sortable: false, align: 'right' },
  { key: 'lead_source_type', label: 'Lead Source', sortable: false },
  { key: 'medical_condition', label: 'Medical Condition', sortable: false },
  { key: 'created_at', label: 'Created', sortable: true },
  { key: 'updated_at', label: 'Last Updated', sortable: true },
  { key: 'last_activity_at', label: 'Last Activity', sortable: true },
  { key: 'tags', label: 'Tags', sortable: false },
  { key: 'contact_email', label: 'Email', sortable: false },
  { key: 'contact_phone', label: 'Phone', sortable: false },
  { key: 'contact_city', label: 'City', sortable: false },
  { key: 'contact_state', label: 'State', sortable: false },
];

// §2 fixed filter vocabularies (P4a)
const VALUE_BANDS = [
  { value: '0-5k',   label: '$0\u20135k',   min: 0,     max: 5000 },
  { value: '5-10k',  label: '$5\u201310k',  min: 5000,  max: 10000 },
  { value: '10-25k', label: '$10\u201325k', min: 10000, max: 25000 },
  { value: '25k+',   label: '$25k+',         min: 25000, max: null },
];
const DATE_PRESETS = ['Today', 'Yesterday', 'This Week', 'Last Week', 'This Month', 'Last Month', 'Last 30 Days', 'Last 90 Days'];
const CLINIC_LOCATIONS = ['Tijuana', 'Cancun', 'Puerto Vallarta'];

// Maps a §2 date preset to [fromISO, toISO] in local time.
function presetRange(preset) {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(now);
  const day = 24 * 60 * 60 * 1000;
  switch (preset) {
    case 'Today':        return [today, null];
    case 'Yesterday':    return [new Date(today - day), today];
    case 'This Week': {  const dow = (today.getDay() + 6) % 7; return [new Date(today - dow * day), null]; }
    case 'Last Week': {  const dow = (today.getDay() + 6) % 7; const mon = new Date(today - dow * day); return [new Date(mon - 7 * day), mon]; }
    case 'This Month':   return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    case 'Last Month':   return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    case 'Last 30 Days': return [new Date(today - 30 * day), null];
    case 'Last 90 Days': return [new Date(today - 90 * day), null];
    default:             return [null, null];
  }
}

const BULK_ACTIONS = [
  { value: 'assign', label: 'Assign to User', icon: UserCheck, needsValue: 'user' },
  { value: 'stage', label: 'Change Stage', icon: TrendingUp, needsValue: 'stage' },
  { value: 'status', label: 'Update Status', icon: CheckSquare, needsValue: 'status' },
  { value: 'add_tag', label: 'Add Tag', icon: Tag, needsValue: 'text' },
  { value: 'remove_tag', label: 'Remove Tag', icon: Minus, needsValue: 'text' },
  { value: 'delete', label: 'Delete', icon: Trash2, needsValue: false, danger: true },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function OpportunitiesTab({ contacts, allUsers, currentUser, pipelines, tags, loadData, loadExtended, canCreate, canUpdate, onContactClick, onContactPreview, stageMoves, clearStageMove }) {
  /*
   * ⚠ Card P95. `openContact` is what every contact-name click in this file
   * goes through: the drawer when the host offers one, the full record
   * otherwise, and the full record either way on a modifier-click (the host
   * reads that from the forwarded event).
   *
   * ⚠ The OPPORTUNITY is passed where one is in scope, because the drawer's
   * Move stage cannot infer which of a contact's opportunities it is looking
   * at. The calendar view has only a contact id, so it passes none and the
   * quick action is simply not offered there.
   */
  /*
   * ⛔⛔ A DRAG IS NOT A CLICK — Card P95 item 3.
   *
   * The board's cards are draggable AND their contact name is clickable. A
   * drag begun ON the name bubbles to the card's onDragStart, and the browser
   * then fires `click` on the name when the drag ends — so every stage move
   * started from the name would throw a drawer over the board the rep was
   * mid-gesture in.
   *
   * ⚠ ONE GUARD PER MOUNT, shared by every card: the gesture is global (there
   * is only one pointer), and a guard per card would reset on re-render.
   */
  const dragGuardRef = useRef(null);
  if (!dragGuardRef.current) dragGuardRef.current = createClickNotDrag();
  const dragGuard = dragGuardRef.current;

  /*
   * ⛔⛔ THIS WAS CALLED AND NEVER DEFINED — Card P95b item 1, and it broke the
   * WHOLE TAB. groupedByStage called withStageMoves(opportunities) and listed
   * it in its deps, so every render of OpportunitiesTab threw a ReferenceError
   * — list, board and calendar alike. CI stayed green because no test mounted
   * the real component: every arm drove a helper or PipelineBoard. A suite
   * that never renders the thing it changed cannot see it stop rendering.
   *
   * What it does: overlay the stage the DRAWER just moved onto whatever is
   * already loaded, so the card lands in its new column with no refetch (item
   * 5 of P95). A loadData() here would re-mount the board, which is the lost
   * place that card removed.
   *
   * ⚠ AND THE OVERLAY CLEARS ITSELF. Once the server's own copy of the row
   * agrees with the overlay, the entry is dropped — otherwise a stale override
   * would outlive the truth and pin a card to a stage it had since left.
   */
  const withStageMoves = useCallback((list) => {
    if (!stageMoves || !Object.keys(stageMoves).length) return list || [];
    return (list || []).map((o) => {
      const moved = stageMoves[o.id];
      if (!moved || moved === o.stage) return o;   // absent, or the server agrees
      return { ...o, stage: moved };
    });
  }, [stageMoves]);


  /*
   * ⛔⛔ THE GUARD IS SCOPED TO THE BOARD CARD — Card P95b item 3.
   *
   * It used to wrap EVERY contact-name click. The `dragged` flag is set by a
   * board drag and is only cleared by the next pointerdown ON A GUARDED
   * ELEMENT — and the list rows and calendar cells are not guarded, because
   * they are not draggable. So a drag on the board followed by a switch to
   * list or calendar left the flag set, and the next name click there did
   * NOTHING, silently, until the user happened to press on a board card again.
   *
   * ⚠ openContact is now the plain path. openContactFromBoardCard runs through
   * dragGuard.guard(), which both checks AND resets — so the flag can never
   * outlive the gesture that set it.
   */
  const openContact = (id, opp, e) => {
    const fn = onContactPreview || onContactClick;
    if (fn) fn(id, { event: e, opportunity: opp || null });
  };

  const openContactFromBoardCard = (id, opp) => dragGuard.guard((e) => openContact(id, opp, e));
  const [opportunities, setOpportunities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  /*
   * ⛔⛔ DEFAULTS TO OPEN — Card P114 (D3).
   *
   * Maria, 24 Oct: no way to tell an active deal from a dead one. The board
   * already HAD a status filter and status badges; what it did not have was a
   * default, so every view opened on open + won + lost + abandoned mixed
   * together.
   *
   * ⚠ THE "open" DEFAULT IN `filterGroups` BELOW IS INERT AND ALWAYS HAS BEEN
   * — it only reaches the query when `useAdvanced` is true (see
   * loadOpportunities), and `useAdvanced` starts false. A reader could easily
   * believe the board already defaulted to open; it never did. THIS line is
   * the one the query reads, via `if (statusFilter) filters.status = …`.
   */
  const [statusFilter, setStatusFilter] = useState('open');
  const [stageFilter, setStageFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [tagsFilter, setTagsFilter] = useState([]);        // §2 tags multi (decrypted, server-side)
  const [stagesFilter, setStagesFilter] = useState([]);    // §2 stage multi
  const [sourceFilter, setSourceFilter] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');    // user id | 'none'
  const [clinicFilter, setClinicFilter] = useState('');
  const [valueBand, setValueBand] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [pipelineFilter, setPipelineFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState(''); // stale days
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [view, setView] = useState('list');
  /*
   * ⚠ AND THE GUARD IS RESET WHEN THE VIEW CHANGES. The board is unmounted on
   * a switch, so no pointerdown will ever arrive to clear a flag left set by
   * the last drag. Belt and braces with the scoping above: the flag can now
   * neither leak across views nor survive its own gesture.
   */
  useEffect(() => { dragGuard.handlers.onPointerDown({}); }, [view, dragGuard]);

  const [visibleFields, setVisibleFields] = useState(DEFAULT_VISIBLE_FIELDS);
  const [showManageFields, setShowManageFields] = useState(false);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editOpp, setEditOpp] = useState(null);
  const [detailOpp, setDetailOpp] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterName, setActiveFilterName] = useState('');

  // Selection / Bulk
  const [selected, setSelected] = useState(new Set());
  const [showBulkMenu, setShowBulkMenu] = useState(false);

  // Advanced filter state
  const [filterGroups, setFilterGroups] = useState([
    { logic: 'AND', conditions: [{ field: 'status', operator: 'is', value: 'open' }] }
  ]);
  const [groupLogic, setGroupLogic] = useState('AND');
  const [useAdvanced, setUseAdvanced] = useState(false);

  // Drag-and-drop
  const [dragItem, setDragItem] = useState(null);

  // ── Unified filter bar (Task #230) ───────────────────────────────────────
  // Bridges CrmFilterBar output into the existing filter-state variables so
  // loadOpportunities re-runs automatically via its useCallback deps.
  const barFilterRef = useRef({});
  const handleBarFilter = useCallback((f) => {
    barFilterRef.current = f;
    if (f.search   !== undefined) setSearch(f.search || '');
    if (f.owner    !== undefined) setOwnerFilter(f.owner ? String(f.owner) : '');
    if (f.stages?.length)         setStageFilter(f.stages[0]);
    else                          setStageFilter('');
    if (f.sortBy   !== undefined) setSortBy(f.sortBy || 'updated_at');
    if (f.sortDir  !== undefined) setSortDir(f.sortDir || 'desc');
  }, []);

  // ── Data Loading ──────────────────────────────────────────────────────────
  const loadOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { search, sortBy, sortDir };
      if (statusFilter) filters.status = statusFilter;
      if (stageFilter) filters.stage = stageFilter;
      if (stagesFilter.length) filters.stages = stagesFilter;
      if (tagsFilter.length) filters.tags = tagsFilter; // decrypted app-layer filter (encrypt ruling)
      if (unassignedOnly) filters.ownerId = 'unassigned';
      else if (ownerFilter) filters.ownerId = ownerFilter;
      if (pipelineFilter) filters.pipelineId = pipelineFilter;
      if (sourceFilter) filters.source = sourceFilter;
      if (doctorFilter) filters.assignedDoctorId = doctorFilter;
      if (clinicFilter) filters.clinicLocation = clinicFilter;
      if (valueBand) {
        const band = VALUE_BANDS.find(b => b.value === valueBand);
        if (band) { filters.valueMin = band.min; if (band.max != null) filters.valueMax = band.max; }
      }
      if (datePreset) {
        const [from, to] = presetRange(datePreset);
        if (from) filters.createdAfter = from.toISOString();
        if (to) filters.createdBefore = to.toISOString();
      }
      if (activityFilter) filters.staleDays = parseInt(activityFilter);
      const bf = barFilterRef.current;
      if (bf.dateField === 'created_at') {
        if (bf.dateFrom) filters.createdAfter = bf.dateFrom;
        if (bf.dateTo)   filters.createdBefore = bf.dateTo;
      }
      if (useAdvanced && filterGroups.length > 0) {
        filters.filterGroups = filterGroups;
        filters.groupLogic = groupLogic;
      }
      const [opps, sum, sf] = await Promise.all([
        window.zignagenixAPI.getCRMOpportunities(filters).catch(() => []),
        window.zignagenixAPI.getCRMOpportunitySummary().catch(() => null),
        window.zignagenixAPI.getCRMOpportunityFilters().catch(() => []),
      ]);
      setOpportunities(Array.isArray(opps) ? opps : []);
      if (sum && !sum.error) setSummary(sum);
      setSavedFilters(Array.isArray(sf) ? sf : []);
      setSelected(new Set());
    } catch (e) { console.error('[Opps] load error:', e); }
    finally { setLoading(false); }
  }, [search, statusFilter, stageFilter, stagesFilter, tagsFilter, ownerFilter, unassignedOnly, pipelineFilter, sourceFilter, doctorFilter, clinicFilter, valueBand, datePreset, activityFilter, sortBy, sortDir, useAdvanced, filterGroups, groupLogic]);

  useEffect(() => { loadOpportunities(); }, [loadOpportunities]);

  // ── Selection ─────────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === opportunities.length) setSelected(new Set());
    else setSelected(new Set(opportunities.map(o => o.id)));
  };
  const allSelected = opportunities.length > 0 && selected.size === opportunities.length;
  const someSelected = selected.size > 0 && selected.size < opportunities.length;

  // ── Bulk Action Handler ───────────────────────────────────────────────────
  const executeBulk = async (action, value) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    if (action === 'delete' && !window.confirm(`Delete ${ids.length} opportunity(s)? This cannot be undone.`)) return;
    try {
      const r = await window.zignagenixAPI.bulkUpdateCRMOpportunities(ids, action, value);
      /*
       * ⛔ The server refuses a WHOLE batch that touches a GHL-managed row —
       * for every action now, not just 'stage' (Card P95g item 3). Nothing was
       * written, so nothing is cleared and no reload is needed.
       */
      if (r && r.error) { alert(r.message || 'The bulk action was refused.'); return; }
      // ⛔ Item 2 — this writer has spoken for these rows.
      if (action === 'stage' && clearStageMove) ids.forEach((id) => clearStageMove(id));
      setShowBulkMenu(false);
      loadOpportunities();
    } catch (e) { alert('Bulk action error: ' + e.message); }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const data = await window.zignagenixAPI.exportCRMOpportunities({});
      if (!Array.isArray(data) || data.length === 0) return alert('No data to export');
      const headers = Object.keys(data[0]);
      const csv = [headers.join(','), ...data.map(row =>
        headers.map(h => {
          const val = row[h] == null ? '' : String(row[h]).replace(/"/g, '""');
          return `"${val}"`;
        }).join(',')
      )].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `opportunities_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert('Export error: ' + e.message); }
  };

  // ── Sort Handler ──────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortBy === field) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortBy('updated_at'); setSortDir('desc'); }
    } else { setSortBy(field); setSortDir('desc'); }
  };

  // ── Drag-and-Drop (Pipeline View) ────────────────────────────────────────
  const handleDragStart = (opp) => setDragItem(opp);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (stage) => {
    if (!dragItem || dragItem.stage === stage) { setDragItem(null); return; }
    try {
      const r = await window.zignagenixAPI.moveCRMOpportunityStage(dragItem.id, stage);
      /*
       * ⛔ A REFUSAL IS NOT A MOVE — the server now refuses a drag on a
       * GHL-managed row, and an optimistic update that ignored that would show
       * the card in a column the database does not agree with.
       */
      if (r && r.success === false) { alert(r.error || 'The move was refused.'); setDragItem(null); return; }
      /*
       * ⛔⛔ THE DRAWER'S OVERLAY MUST GO — Card P95c item 2. This writer has
       * now spoken for the row, so a stale { id: 'proposal' } from an earlier
       * drawer move would put the card straight back and then jam the next
       * drop on handleDrop's own early return.
       */
      if (clearStageMove) clearStageMove(dragItem.id);
      // Optimistic update
      setOpportunities(prev => prev.map(o => o.id === dragItem.id ? { ...o, stage } : o));
    } catch (e) { alert('Move error: ' + e.message); }
    setDragItem(null);
  };

  /*
   * ⛔⛔ AND AN OVERLAY THE SERVER AGREES WITH IS ACTUALLY DELETED — Card P95c
   * item 2.
   *
   * ⚠⚠ MY P95b COMMENT CLAIMED THIS ALREADY HAPPENED. It did not:
   * withStageMoves merely SKIPPED an entry whose value matched, so the entry
   * stayed in the map for ever and kept masking later writes from other
   * surfaces. "Dropped" in a comment is not dropped.
   *
   * Runs after each load, against the rows the server actually returned.
   */
  useEffect(() => {
    if (!clearStageMove || !stageMoves || !Object.keys(stageMoves).length) return;
    for (const o of opportunities || []) {
      if (stageMoves[o.id] !== undefined && stageMoves[o.id] === o.stage) clearStageMove(o.id);
    }
  }, [opportunities, stageMoves, clearStageMove]);

  /*
   * ⛔⛔ THE SERVER'S ROW, NOT THE OVERLAID ONE — Card P95f item 3.
   *
   * The list renders withStageMoves(opportunities), so each row carries the
   * DRAWER'S optimistic stage. Seeding the edit form or the detail modal from
   * that row means a stale overlay can be written straight back over a move
   * somebody else has since made. The overlay exists to paint a card between a
   * drawer move and the next load; it is not a source for a WRITE.
   */
  const serverRow = useCallback(
    (overlaid) => (opportunities || []).find((o) => o.id === overlaid.id) || overlaid,
    [opportunities]);

  // ── Pipeline View Grouping ────────────────────────────────────────────────
  const groupedByStage = useMemo(() => {
    const groups = {};
    DEFAULT_STAGES.forEach(s => { groups[s] = []; });
    // ⚠ The drawer's stage move is overlaid here, so the card lands in its new
    // column without a refetch — see withStageMoves.
    withStageMoves(opportunities).forEach(o => {
      const s = o.stage || 'lead';
      if (!groups[s]) groups[s] = [];
      groups[s].push(o);
    });
    return groups;
  }, [withStageMoves, opportunities]);

  // ── Unique tags from opportunities ────────────────────────────────────────
  const allOppTags = useMemo(() => {
    const tagSet = new Set();
    opportunities.forEach(o => {
      if (o.tags) o.tags.split(',').forEach(t => { const tt = t.trim(); if (tt) tagSet.add(tt); });
    });
    return Array.from(tagSet).sort();
  }, [opportunities]);

  // ── Unique sources from opportunities (§2 source filter options) ─────────
  const allOppSources = useMemo(() => {
    const srcSet = new Set();
    opportunities.forEach(o => { if (o.contact_source) srcSet.add(o.contact_source); });
    return Array.from(srcSet).sort();
  }, [opportunities]);

  // ── Format Helpers ────────────────────────────────────────────────────────
  const fmt = (v) => v != null && v !== '' ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ═══ Unified Filter Bar (Task #230) ═══ */}
      <CrmFilterBar
        resource="opportunities"
        allUsers={allUsers}
        currentUser={currentUser}
        onFilterChange={handleBarFilter}
      />

      {/* KPIs */}
      {summary && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <MiniKPI icon={Target} label="Total Deals" value={summary.total} color="#2E8B98" />
          <MiniKPI icon={DollarSign} label="Open Pipeline" value={fmt(summary.open?.value)} color="#F26522" sub={`${summary.open?.count} deals`} />
          <MiniKPI icon={Award} label="Won" value={fmt(summary.won?.value)} color="#4FA038" sub={`${summary.won?.count} deals`} />
          <MiniKPI icon={AlertTriangle} label="Lost" value={summary.lost} color="#DC3545" />
        </div>
      )}

      {/* ═══ TOOLBAR ROW 1: Pipeline + Status Filter ═══ */}
      <div style={{ ...cardWrap, padding: 14, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* 1. Pipeline Selector */}
          <select value={pipelineFilter} onChange={e => setPipelineFilter(e.target.value)}
            style={{ ...inp, width: 'auto', minWidth: 140, fontWeight: 600, color: '#2E8B98' }}>
            <option value="">All Pipelines</option>
            {(pipelines || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          {/* 9. Status Filter */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ ...inp, width: 'auto', minWidth: 100 }}>
            {/* Card P114 (D3): named so the way out of the new default is
                obvious — "All Status" read as a label, not a choice. */}
            <option value="">All statuses</option>
            {Object.entries(OPP_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>

          {/* §2 Date preset (created date) */}
          <select value={datePreset} onChange={e => setDatePreset(e.target.value)}
            style={{ ...inp, width: 'auto', minWidth: 110 }}>
            <option value="">All Time</option>
            {DATE_PRESETS.map(pr => <option key={pr} value={pr}>{pr}</option>)}
          </select>

          {/* §2 Source */}
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
            style={{ ...inp, width: 'auto', minWidth: 100 }}>
            <option value="">All Sources</option>
            {allOppSources.map(src => <option key={src} value={src}>{src}</option>)}
          </select>

          {/* §2 Assigned doctor */}
          <select value={doctorFilter} onChange={e => setDoctorFilter(e.target.value)}
            style={{ ...inp, width: 'auto', minWidth: 110 }}>
            <option value="">All Doctors</option>
            <option value="none">Not Assigned</option>
            {(allUsers || []).map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
          </select>

          {/* §2 Clinic location */}
          <select value={clinicFilter} onChange={e => setClinicFilter(e.target.value)}
            style={{ ...inp, width: 'auto', minWidth: 100 }}>
            <option value="">All Clinics</option>
            {CLINIC_LOCATIONS.map(cl => <option key={cl} value={cl}>{cl}</option>)}
          </select>

          {/* §2 Deal value band */}
          <select value={valueBand} onChange={e => setValueBand(e.target.value)}
            style={{ ...inp, width: 'auto', minWidth: 90 }}>
            <option value="">Any Value</option>
            {VALUE_BANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>

          {/* §2 Ownership: Unassigned */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6C757D', cursor: 'pointer' }}>
            <input type="checkbox" checked={unassignedOnly} onChange={e => setUnassignedOnly(e.target.checked)} />
            Unassigned
          </label>
        </div>

        {/* ═══ TOOLBAR ROW 2: Tags + Activity + Actions ═══ */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
          {/* §2 Stage multi-select */}
          <MultiSelect label="Stages" options={DEFAULT_STAGES.map(st => ({ value: st, label: STAGE_LABELS[st] || st }))}
            selected={stagesFilter} onChange={setStagesFilter} />

          {/* §2 Tags multi-select — evaluated server-side over DECRYPTED values */}
          <MultiSelect label="Tags" options={allOppTags.map(t => ({ value: t, label: t }))}
            selected={tagsFilter} onChange={setTagsFilter} />

          {/* 11. Activity / Stale Deals */}
          <select value={activityFilter} onChange={e => setActivityFilter(e.target.value)}
            style={{ ...inp, width: 'auto', minWidth: 120 }}>
            <option value="">All Activity</option>
            <option value="7">Stale 7+ days</option>
            <option value="14">Stale 14+ days</option>
            <option value="30">Stale 30+ days</option>
            <option value="60">Stale 60+ days</option>
          </select>

          {/* 2. Advanced Filter */}
          <button onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
            style={{ ...btnSm, color: useAdvanced ? '#2E8B98' : '#6C757D', fontWeight: useAdvanced ? 700 : 500 }}>
            <Sliders size={13} /> Advanced
          </button>

          <div style={{ flex: 1 }} />

          {/* 4. View Switcher */}
          {[['list', LayoutList, 'List'], ['pipeline', Columns3, 'Board'], ['calendar', CalendarDays, 'Calendar']].map(([v, Icon, lbl]) => (
            <button key={v} onClick={() => setView(v)}
              style={{ ...btnSm, ...(view === v ? { background: '#E7F4F6', color: '#2E8B98' } : {}) }}>
              <Icon size={13} /><span style={{ marginLeft: 3 }}>{lbl}</span>
            </button>
          ))}

          {/* 6. Manage Fields */}
          <button onClick={() => setShowManageFields(true)} style={btnSm}><Eye size={13} /> Fields</button>

          {/* 12. Export */}
          <button onClick={handleExport} style={btnSm}><Download size={13} /> Export</button>

          {/* Refresh */}
          <button onClick={loadOpportunities} style={btnSm}><RefreshCw size={13} /></button>

          {/* 7. Create */}
          {canCreate && (
            <button onClick={() => setShowCreateModal(true)} style={btnPrimary}>
              <Plus size={14} /> New Deal
            </button>
          )}
        </div>

        {/* Saved Smart Lists */}
        {savedFilters.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#6C757D', alignSelf: 'center' }}>Smart Lists:</span>
            {savedFilters.map(f => (
              <button key={f.id} onClick={() => {
                try {
                  const cfg = typeof f.filter_config === 'string' ? JSON.parse(f.filter_config) : f.filter_config;
                  if (cfg.groups) { setFilterGroups(cfg.groups); setGroupLogic(cfg.groupLogic || 'AND'); }
                  setUseAdvanced(true); setActiveFilterName(f.name); setShowAdvancedFilter(true);
                } catch { /* */ }
              }}
                style={{ ...btnSm, fontSize: 11, padding: '4px 10px',
                  background: activeFilterName === f.name ? '#E0F2FE' : undefined,
                  color: activeFilterName === f.name ? '#2E8B98' : '#6C757D' }}>
                {f.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 3. Bulk Actions Bar */}
      {selected.size > 0 && (
        <div style={{ ...cardWrap, padding: '10px 16px', marginBottom: 12, background: '#EFF6FF',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderLeft: '3px solid #2E8B98' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#2E8B98' }}>{selected.size} selected</span>
          {BULK_ACTIONS.map(ba => (
            <BulkActionButton key={ba.value} action={ba} allUsers={allUsers}
              onExecute={(val) => executeBulk(ba.value, val)} />
          ))}
          <button onClick={() => setSelected(new Set())} style={{ ...btnSm, fontSize: 11, marginLeft: 'auto' }}>
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* Advanced Filter Panel */}
      {showAdvancedFilter && (
        <AdvancedFilterPanel
          filterGroups={filterGroups} setFilterGroups={setFilterGroups}
          groupLogic={groupLogic} setGroupLogic={setGroupLogic}
          useAdvanced={useAdvanced} setUseAdvanced={setUseAdvanced}
          onApply={loadOpportunities}
          onSave={async (name) => {
            await window.zignagenixAPI.saveCRMOpportunityFilter({
              name, filterConfig: { groups: filterGroups, groupLogic }, isShared: true,
            });
            setActiveFilterName(name); loadOpportunities();
          }}
          onClose={() => setShowAdvancedFilter(false)}
        />
      )}

      {/* ═══ CONTENT ═══ */}
      {loading ? (
        <div style={emptyMsg}>Loading opportunities...</div>
      ) : view === 'calendar' ? (
        /* ═══ §3 CALENDAR VIEW (P4b) — pipeline events: consultations, therapy, follow-ups ═══ */
        <OppCalendarView onContactClick={(id, e) => openContact(id, null, e)} />
      ) : opportunities.length === 0 ? (
        <div style={{ ...cardWrap, ...emptyMsg }}>
          <Target size={32} color="#ADB5BD" style={{ marginBottom: 8 }} />
          <div>No opportunities found. {canCreate ? 'Create your first deal!' : ''}</div>
        </div>
      ) : view === 'list' ? (
        /* ═══ LIST VIEW ═══ */
        <div className="responsive-scroll" style={{ ...cardWrap, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 720 }}>
            <thead>
              <tr style={{ background: '#F0F4F8' }}>
                {/* Checkbox */}
                <th style={{ padding: '10px 8px', width: 36, borderBottom: '2px solid #E5E7EB' }}>
                  <button onClick={toggleAll} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    {allSelected ? <CheckSquare size={15} color="#2E8B98" /> : someSelected ? <Minus size={15} color="#2E8B98" /> : <Square size={15} color="#ADB5BD" />}
                  </button>
                </th>
                {ALL_COLUMNS.filter(c => visibleFields.includes(c.key)).map(col => (
                  <th key={col.key} onClick={() => col.sortable && handleSort(col.key === 'owner_name' ? 'owner' : col.key)}
                    style={{ padding: '10px 12px', textAlign: col.align || 'left', fontWeight: 600, color: '#374151',
                      cursor: col.sortable ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap', borderBottom: '2px solid #E5E7EB' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {col.label}
                      {col.sortable && sortBy === (col.key === 'owner_name' ? 'owner' : col.key) && (
                        sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />
                      )}
                    </span>
                  </th>
                ))}
                <th style={{ padding: '10px 12px', width: 70, borderBottom: '2px solid #E5E7EB' }}></th>
              </tr>
            </thead>
            <tbody>
              {/* ⚠ The overlay applies here as well — a stage moved from the
                   drawer must show in the list, not only on the board. */}
              {/* ⚠ `opp` here is the OVERLAID row — see serverRow below. */}
              {withStageMoves(opportunities).map((opp, idx) => (
                <tr key={opp.id}
                  style={{ background: selected.has(opp.id) ? '#EFF6FF' : idx % 2 === 0 ? '#fff' : '#FAFBFC', cursor: 'pointer' }}
                  onMouseEnter={e => { if (!selected.has(opp.id)) e.currentTarget.style.background = '#F0F9FF'; }}
                  onMouseLeave={e => { if (!selected.has(opp.id)) e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#FAFBFC'; }}>
                  {/* Checkbox */}
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #F3F4F6' }}>
                    <button onClick={() => toggleSelect(opp.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      {selected.has(opp.id) ? <CheckSquare size={15} color="#2E8B98" /> : <Square size={15} color="#D1D5DB" />}
                    </button>
                  </td>
                  {ALL_COLUMNS.filter(c => visibleFields.includes(c.key)).map(col => (
                    <td key={col.key} onClick={() => setDetailOpp(serverRow(opp))}
                      style={{ padding: '10px 12px', textAlign: col.align || 'left',
                        borderBottom: '1px solid #F3F4F6', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {renderCell(opp, col.key, (id, e) => openContact(id, opp, e), !!(onContactPreview || onContactClick))}
                    </td>
                  ))}
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #F3F4F6' }}>
                    {/* ⚠ An ACCESSIBLE NAME on an icon-only control — Card P95d.
                        It had none, so neither a screen reader nor a test could
                        name it; the row just held an unlabelled button. */}
                    {canUpdate && <button onClick={(e) => { e.stopPropagation(); setEditOpp(serverRow(opp)); }}
                      aria-label={`Edit ${opp.title || 'opportunity'}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                      <Edit2 size={13} color="#6C757D" />
                    </button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '8px 14px', fontSize: 11, color: '#ADB5BD', borderTop: '1px solid #F3F4F6' }}>
            Showing {opportunities.length} deal{opportunities.length !== 1 ? 's' : ''} | Pipeline value: {fmt(opportunities.reduce((s, o) => s + Number(o.value || 0), 0))}
          </div>
        </div>
      ) : (
        /* ═══ PIPELINE / BOARD VIEW (with drag-and-drop) ═══ */
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
          {DEFAULT_STAGES.map(stage => {
            const stageOpps = groupedByStage[stage] || [];
            const stageValue = stageOpps.reduce((sum, o) => sum + Number(o.value || 0), 0);
            return (
              <div key={stage} style={{ minWidth: 260, flex: '0 0 260px' }}
                onDragOver={handleDragOver}
                data-drop-stage={stage}
                onDrop={() => handleDrop(stage)}>
                {/* Stage Header */}
                <div style={{ ...cardWrap, padding: '10px 14px', marginBottom: 8,
                  borderTop: `3px solid ${STAGE_COLORS[stage] || '#6C757D'}`,
                  background: dragItem && dragItem.stage !== stage ? '#F0F9FF' : '#F8F9FA' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: STAGE_COLORS[stage] }}>{STAGE_LABELS[stage] || stage}</span>
                    <span style={{ fontSize: 11, color: '#6C757D', background: '#E5E7EB', borderRadius: 10, padding: '1px 8px' }}>{stageOpps.length}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#ADB5BD', marginTop: 2 }}>{fmt(stageValue)}</div>
                </div>

                {/* Cards */}
                {stageOpps.map(opp => (
                  /* ⛔⛔ A GHL DEAL IS NOT DRAGGABLE — Card P95g item 2. The
                     server refuses the move, but an affordance that refuses is
                     a promise the screen cannot keep: the rep drags the card,
                     it springs back, and nothing explains why. ⚠ The real
                     guard is the server's; this stops the rep being invited to
                     try. */
                  <div key={opp.id} draggable={!isGhlManagedRow(opp)}
                    title={isGhlManagedRow(opp) ? GHL_MANAGED_SENTENCE : undefined}
                    onPointerDown={dragGuard.handlers.onPointerDown}
                    onDragStart={(e) => {
                      if (isGhlManagedRow(opp)) { e.preventDefault(); return; }
                      dragGuard.handlers.onDragStart(e); handleDragStart(opp);
                    }}
                    onDragEnd={dragGuard.handlers.onDragEnd}
                    onClick={() => setDetailOpp(serverRow(opp))}
                    style={{ ...cardWrap, padding: '12px 14px', marginBottom: 8, cursor: 'grab',
                      transition: 'transform 0.1s, box-shadow 0.1s',
                      opacity: dragItem?.id === opp.id ? 0.5 : 1 }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '8px 8px 16px rgba(186,199,216,0.55), -8px -8px 16px rgba(255,255,255,0.9)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = neuShadow; }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.title}</div>
                    {opp.contact_name && <div style={{ fontSize: 11, color: opp.contact_id && (onContactPreview || onContactClick) ? '#2E8B98' : '#6C757D', marginBottom: 6, cursor: opp.contact_id && (onContactPreview || onContactClick) ? 'pointer' : 'default' }}
                      onClick={opp.contact_id && (onContactPreview || onContactClick) ? (e) => { e.stopPropagation(); openContactFromBoardCard(opp.contact_id, opp)(e); } : undefined}>{opp.contact_name}</div>}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#212529' }}>{fmt(opp.value)}</span>
                      <Badge text={OPP_STATUSES[opp.status]?.label || opp.status} color={OPP_STATUSES[opp.status]?.color || '#6C757D'} bg={OPP_STATUSES[opp.status]?.bg} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                      {opp.owner_name && <span style={{ fontSize: 10, color: '#ADB5BD' }}>{opp.owner_name}</span>}
                      {opp.treatment_type && <Badge text={opp.treatment_type} color="#256F7A" />}
                    </div>
                    {opp.tags && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                        {opp.tags.split(',').slice(0, 3).map((t, i) => (
                          <span key={i} style={{ fontSize: 9, background: '#E5E7EB', color: '#6C757D', borderRadius: 6, padding: '1px 6px' }}>{t.trim()}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {stageOpps.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#D1D5DB', fontSize: 11,
                    border: '2px dashed #E5E7EB', borderRadius: 10, margin: '0 4px' }}>
                    Drop deals here
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ MODALS ═══ */}
      {showManageFields && <ManageFieldsModal visibleFields={visibleFields} setVisibleFields={setVisibleFields} onClose={() => setShowManageFields(false)} />}
      {showCreateModal && <OppFormModal contacts={contacts} users={allUsers} pipelines={pipelines} onClose={() => setShowCreateModal(false)} onSaved={() => { setShowCreateModal(false); loadOpportunities(); }} />}
      {editOpp && <OppFormModal opp={editOpp} contacts={contacts} users={allUsers} pipelines={pipelines} onStageWritten={clearStageMove} onClose={() => setEditOpp(null)} onSaved={() => { setEditOpp(null); loadOpportunities(); }} />}
      {detailOpp && <OppDetailModal opp={detailOpp} onClose={() => setDetailOpp(null)} onEdit={() => { setEditOpp(detailOpp); setDetailOpp(null); }} />}
    </div>
  );
}

const ghlNoteStyle = { margin: '10px 0 14px', padding: '9px 12px', borderRadius: 8, background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E40AF', fontSize: 12, lineHeight: 1.45 };
const saveErrStyle = { marginTop: 14, padding: '9px 12px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', fontSize: 12, lineHeight: 1.45 };

// ─── Cell Renderer ───────────────────────────────────────────────────────────

function renderCell(opp, key, onOpenContact, hasOpener) {
  switch (key) {
    case 'value': case 'budget':
      return opp[key] != null ? `$${Number(opp[key]).toLocaleString()}` : '—';
    case 'stage':
      return <Badge text={STAGE_LABELS[opp.stage] || opp.stage} color={STAGE_COLORS[opp.stage] || '#6C757D'} />;
    case 'status':
      return <Badge text={OPP_STATUSES[opp.status]?.label || opp.status} color={OPP_STATUSES[opp.status]?.color} bg={OPP_STATUSES[opp.status]?.bg} />;
    case 'probability': return opp.probability != null ? `${opp.probability}%` : '—';
    case 'qualification_score': return opp.qualification_score || '—';
    case 'created_at': case 'updated_at': case 'last_activity_at':
      return opp[key] ? new Date(opp[key]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    case 'contact_name': {
      const name = opp.contact_name || (opp.c_first ? `${opp.c_first} ${opp.c_last}` : '—');
      return opp.contact_id && hasOpener
        ? <span onClick={(e) => { e.stopPropagation(); onOpenContact(opp.contact_id, e); }} style={{ color: '#2E8B98', cursor: 'pointer', textDecoration: 'underline' }}>{name}</span>
        : name;
    }
    case 'tags':
      if (!opp.tags) return '—';
      return <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {opp.tags.split(',').slice(0, 3).map((t, i) => <span key={i} style={{ fontSize: 10, background: '#E5E7EB', color: '#6C757D', borderRadius: 6, padding: '1px 6px' }}>{t.trim()}</span>)}
      </div>;
    default: return opp[key] || '—';
  }
}

// ─── Bulk Action Button ──────────────────────────────────────────────────────

function BulkActionButton({ action, allUsers, onExecute }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState('');
  const Icon = action.icon;

  if (!action.needsValue) {
    return <button onClick={() => onExecute(null)} style={{ ...btnSm, fontSize: 11, ...(action.danger ? { color: '#DC3545' } : {}) }}>
      <Icon size={12} /> {action.label}
    </button>;
  }

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{ ...btnSm, fontSize: 11 }}>
        <Icon size={12} /> {action.label} <ChevronDown size={10} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50, background: '#fff', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: 10, minWidth: 180, marginTop: 4 }}>
          {action.needsValue === 'user' && (
            <select value={val} onChange={e => { onExecute(e.target.value); setOpen(false); }} style={{ ...inp, fontSize: 11 }}>
              <option value="">Select user...</option>
              {(allUsers || []).map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
            </select>
          )}
          {action.needsValue === 'stage' && (
            <select value={val} onChange={e => { onExecute(e.target.value); setOpen(false); }} style={{ ...inp, fontSize: 11 }}>
              <option value="">Select stage...</option>
              {DEFAULT_STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
          )}
          {action.needsValue === 'status' && (
            <select value={val} onChange={e => { onExecute(e.target.value); setOpen(false); }} style={{ ...inp, fontSize: 11 }}>
              <option value="">Select status...</option>
              {Object.entries(OPP_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          )}
          {action.needsValue === 'text' && (
            <div style={{ display: 'flex', gap: 4 }}>
              <input value={val} onChange={e => setVal(e.target.value)} placeholder="Tag name..."
                style={{ ...inp, fontSize: 11, flex: 1 }} onKeyDown={e => { if (e.key === 'Enter' && val.trim()) { onExecute(val.trim()); setOpen(false); setVal(''); } }} />
              <button onClick={() => { if (val.trim()) { onExecute(val.trim()); setOpen(false); setVal(''); } }}
                style={{ ...btnSm, fontSize: 11 }}>Go</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mini KPI ────────────────────────────────────────────────────────────────

function MiniKPI({ icon: Icon, label, value, color, sub }) {
  return (
    <div style={{ background: '#F8F9FA', borderRadius: 12, padding: '12px 16px', boxShadow: neuShadow, flex: '1 1 140px', minWidth: 130 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#6C757D', fontWeight: 500 }}>{label}</span>
        <Icon size={14} color={color} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#212529' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#ADB5BD', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Advanced Filter Panel ───────────────────────────────────────────────────

function AdvancedFilterPanel({ filterGroups, setFilterGroups, groupLogic, setGroupLogic, useAdvanced, setUseAdvanced, onApply, onSave, onClose }) {
  const [saveName, setSaveName] = useState('');

  const addGroup = () => setFilterGroups([...filterGroups, { logic: 'AND', conditions: [{ field: 'value', operator: 'greater_than', value: '' }] }]);
  const removeGroup = (gi) => setFilterGroups(filterGroups.filter((_, i) => i !== gi));
  const updateGroupLogicLocal = (gi, logic) => setFilterGroups(filterGroups.map((g, i) => i === gi ? { ...g, logic } : g));
  const addCondition = (gi) => setFilterGroups(filterGroups.map((g, i) =>
    i === gi ? { ...g, conditions: [...g.conditions, { field: 'title', operator: 'contains', value: '' }] } : g));
  const removeCondition = (gi, ci) => setFilterGroups(filterGroups.map((g, i) =>
    i === gi ? { ...g, conditions: g.conditions.filter((_, j) => j !== ci) } : g));
  const updateCondition = (gi, ci, updates) => setFilterGroups(filterGroups.map((g, i) =>
    i === gi ? { ...g, conditions: g.conditions.map((c, j) => j === ci ? { ...c, ...updates } : c) } : g));

  return (
    <div style={{ ...cardWrap, padding: 16, marginBottom: 12, borderLeft: '3px solid #2E8B98' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sliders size={15} color="#2E8B98" />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>Advanced Filters</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6C757D', cursor: 'pointer' }}>
            <input type="checkbox" checked={useAdvanced} onChange={e => setUseAdvanced(e.target.checked)} /> Active
          </label>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} color="#6C757D" /></button>
      </div>

      {filterGroups.length > 1 && (
        <div style={{ marginBottom: 10, fontSize: 12 }}>
          <span style={{ color: '#6C757D', marginRight: 8 }}>Match:</span>
          <button onClick={() => setGroupLogic('AND')} style={{ ...btnSm, fontSize: 11, padding: '3px 10px', background: groupLogic === 'AND' ? '#E0F2FE' : undefined, color: groupLogic === 'AND' ? '#2E8B98' : '#6C757D' }}>ALL groups (AND)</button>
          <button onClick={() => setGroupLogic('OR')} style={{ ...btnSm, fontSize: 11, padding: '3px 10px', marginLeft: 4, background: groupLogic === 'OR' ? '#FEF3C7' : undefined, color: groupLogic === 'OR' ? '#D97706' : '#6C757D' }}>ANY group (OR)</button>
        </div>
      )}

      {filterGroups.map((group, gi) => (
        <div key={gi} style={{ background: '#F0F4F8', borderRadius: 10, padding: 12, marginBottom: 8, border: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Group {gi + 1}</span>
              <button onClick={() => updateGroupLogicLocal(gi, 'AND')} style={{ ...btnSm, fontSize: 10, padding: '2px 8px', background: group.logic === 'AND' ? '#DBEAFE' : undefined, color: group.logic === 'AND' ? '#2563EB' : '#6C757D' }}>AND</button>
              <button onClick={() => updateGroupLogicLocal(gi, 'OR')} style={{ ...btnSm, fontSize: 10, padding: '2px 8px', background: group.logic === 'OR' ? '#FEF3C7' : undefined, color: group.logic === 'OR' ? '#D97706' : '#6C757D' }}>OR</button>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => addCondition(gi)} style={{ ...btnSm, fontSize: 10, padding: '2px 8px' }}>+ Condition</button>
              {filterGroups.length > 1 && <button onClick={() => removeGroup(gi)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={13} color="#DC3545" /></button>}
            </div>
          </div>

          {group.conditions.map((cond, ci) => {
            const fieldDef = FILTER_FIELDS.find(f => f.value === cond.field) || FILTER_FIELDS[0];
            const operators = OPERATORS_BY_TYPE[fieldDef.type] || OPERATORS_BY_TYPE.text;
            const needsValue = !['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(cond.operator);
            const isBetween = cond.operator === 'between';

            return (
              <div key={ci} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                {ci > 0 && <span style={{ fontSize: 10, color: '#ADB5BD', width: 28, textAlign: 'center' }}>{group.logic}</span>}
                {ci === 0 && <span style={{ width: 28 }} />}
                <select value={cond.field} onChange={e => updateCondition(gi, ci, { field: e.target.value, operator: (OPERATORS_BY_TYPE[(FILTER_FIELDS.find(f => f.value === e.target.value) || {}).type] || OPERATORS_BY_TYPE.text)[0].value, value: '' })}
                  style={{ ...inp, width: 140, fontSize: 11, padding: '5px 8px' }}>
                  {FILTER_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select value={cond.operator} onChange={e => updateCondition(gi, ci, { operator: e.target.value })}
                  style={{ ...inp, width: 120, fontSize: 11, padding: '5px 8px' }}>
                  {operators.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {needsValue && !isBetween && (
                  fieldDef.options ? (
                    <select value={cond.value} onChange={e => updateCondition(gi, ci, { value: e.target.value })}
                      style={{ ...inp, width: 140, fontSize: 11, padding: '5px 8px' }}>
                      <option value="">Select...</option>
                      {fieldDef.options.map(o => <option key={o} value={o}>{STAGE_LABELS[o] || OPP_STATUSES[o]?.label || o}</option>)}
                    </select>
                  ) : (
                    <input value={cond.value || ''} onChange={e => updateCondition(gi, ci, { value: e.target.value })}
                      type={fieldDef.type === 'number' ? 'number' : fieldDef.type === 'date' ? 'date' : 'text'}
                      placeholder={fieldDef.type === 'number' ? '0' : fieldDef.type === 'date' ? 'YYYY-MM-DD' : 'value...'}
                      style={{ ...inp, width: 140, fontSize: 11, padding: '5px 8px' }} />
                  )
                )}
                {needsValue && isBetween && (
                  <>
                    <input value={Array.isArray(cond.value) ? cond.value[0] : ''} onChange={e => updateCondition(gi, ci, { value: [e.target.value, Array.isArray(cond.value) ? cond.value[1] : ''] })}
                      type={fieldDef.type === 'date' ? 'date' : 'number'} placeholder="from"
                      style={{ ...inp, width: 90, fontSize: 11, padding: '5px 8px' }} />
                    <span style={{ fontSize: 10, color: '#ADB5BD' }}>to</span>
                    <input value={Array.isArray(cond.value) ? cond.value[1] : ''} onChange={e => updateCondition(gi, ci, { value: [Array.isArray(cond.value) ? cond.value[0] : '', e.target.value] })}
                      type={fieldDef.type === 'date' ? 'date' : 'number'} placeholder="to"
                      style={{ ...inp, width: 90, fontSize: 11, padding: '5px 8px' }} />
                  </>
                )}
                {group.conditions.length > 1 && (
                  <button onClick={() => removeCondition(gi, ci)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}><X size={12} color="#DC3545" /></button>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <button onClick={addGroup} style={{ ...btnSm, fontSize: 11 }}>+ Filter Group</button>
        <button onClick={onApply} style={{ ...btnPrimary, fontSize: 11, padding: '6px 14px' }}><Filter size={12} /> Apply</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
          <input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Smart list name..."
            style={{ ...inp, width: 140, fontSize: 11, padding: '5px 8px' }} />
          <button onClick={() => { if (saveName.trim()) { onSave(saveName.trim()); setSaveName(''); } }}
            disabled={!saveName.trim()} style={{ ...btnSm, fontSize: 11 }}><Save size={11} /> Save</button>
        </div>
        <button onClick={() => { setFilterGroups([{ logic: 'AND', conditions: [{ field: 'status', operator: 'is', value: 'open' }] }]); setUseAdvanced(false); onApply(); }}
          style={{ ...btnSm, fontSize: 11, color: '#DC3545' }}>Clear All</button>
      </div>
    </div>
  );
}

// ─── Manage Fields Modal ─────────────────────────────────────────────────────

function ManageFieldsModal({ visibleFields, setVisibleFields, onClose }) {
  const [fields, setFields] = useState([...visibleFields]);

  const toggle = (key) => {
    if (fields.includes(key)) setFields(fields.filter(f => f !== key));
    else setFields([...fields, key]);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 440 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>Manage Visible Fields</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="#6C757D" /></button>
        </div>
        <p style={{ fontSize: 12, color: '#6C757D', marginBottom: 12 }}>
          Choose which columns appear in List View and which fields show on Pipeline cards.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {ALL_COLUMNS.map(col => (
            <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
              borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#374151',
              background: fields.includes(col.key) ? '#E0F2FE' : '#F9FAFB',
              border: `1px solid ${fields.includes(col.key) ? '#93C5FD' : '#E5E7EB'}` }}>
              <input type="checkbox" checked={fields.includes(col.key)} onChange={() => toggle(col.key)} />
              {col.label}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={() => setFields(DEFAULT_VISIBLE_FIELDS)} style={btnSm}>Reset Defaults</button>
          <button onClick={() => { setVisibleFields(fields); onClose(); }} style={btnPrimary}><Check size={13} /> Apply</button>
        </div>
      </div>
    </div>
  );
}

// ─── Opportunity Form Modal (Create / Edit) ──────────────────────────────────

function OppFormModal({ opp, contacts = [], users = [], pipelines = [], onClose, onSaved, onStageWritten }) {
  const isEdit = !!opp;
  const [form, setForm] = useState({
    title: opp?.title || '', value: opp?.value || '', status: opp?.status || 'open',
    stage: opp?.stage || 'lead',
    /*
     * ⛔ `??`, and 50 ONLY ON CREATE — Card P95f item 2. `|| 50` turned a
     * stored probability of 0 — which is what a lost deal has — into 50 the
     * moment anyone opened the form.
     */
    probability: opp ? (opp.probability ?? 50) : 50,
    contactId: opp?.contact_id || '', ownerId: opp?.owner_id || '',
    pipelineId: opp?.pipeline_id || '', expectedCloseDate: opp?.expected_close_date ? opp.expected_close_date.split('T')[0] : '',
    treatmentType: opp?.treatment_type || '', budget: opp?.budget || '',
    qualificationScore: opp?.qualification_score ?? '', leadSourceType: opp?.lead_source_type || '',
    /*
     * ⛔ ROUND-TRIPPED — Card P95f item 2. The form never carried these three,
     * so the server had nothing to echo back and blanked them on every save.
     * City is a list column AND a filter.
     */
    contactCity: opp?.contact_city ?? '', contactState: opp?.contact_state ?? '',
    contactSource: opp?.contact_source ?? '',
    medicalCondition: opp?.medical_condition || '', tags: opp?.tags || '', notes: opp?.notes || '',
    lostReason: opp?.lost_reason || '',
    contactName: opp?.contact_name || '', contactEmail: opp?.contact_email || '',
    contactPhone: opp?.contact_phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  /*
   * ⚠ The picker reads `allContacts`, not the `contacts` prop, so a contact
   * created inline is selectable immediately. The prop is the source of truth
   * on every open; this only ADDS to it for the life of the modal.
   */
  const [localContacts, setLocalContacts] = useState([]);
  const allContacts = useMemo(
    () => (localContacts.length ? [...localContacts, ...(contacts || [])] : (contacts || [])),
    [localContacts, contacts],
  );

  const selectedContact = useMemo(
    () => allContacts.find(c => String(c.id) === String(form.contactId)) || null,
    [allContacts, form.contactId],
  );

  /*
   * ⛔⛔ ONE WRITER. The inline "+ New contact" calls the SAME channel that
   * Contacts → Add Contact calls — `crm:contacts:create` (routes/crm.js:1502)
   * — so validation, the audit row, the encryption path and the GHL rules are
   * identical by construction rather than by resemblance. A second INSERT here
   * would be a second policy, and the two would drift.
   *
   * ⚠ NOTHING OUTBOUND TO GHL IS ADDED: this is the existing create path,
   * unchanged, which is inbound-only like the rest of the sync.
   */
  const createContactInline = async (fields) => {
    // ⚠ SAME BRIDGE METHOD AS Contacts → Add Contact (CRMPage.js:135), and the
    // same `sanitize` shape it applies before sending.
    const sanitize = (v) => (typeof v === 'string' ? v.trim().replace(/[<>]/g, '') : v);
    const res = await window.zignagenixAPI.createCRMContact({
      firstName: sanitize(fields.firstName),
      lastName: sanitize(fields.lastName),
      email: sanitize(fields.email),
      phone: sanitize(fields.phone),
      referralAnswer: fields.referralAnswer,
      contactType: 'lead',
    });
    // ⚠ The handler answers { error: true, code, message } on refusal and
    // { success: true, contactId } on success — it does NOT return the row.
    if (!res || res.error) return res || { error: true, message: 'No response.' };
    const created = {
      id: res.contactId,
      first_name: sanitize(fields.firstName),
      last_name: sanitize(fields.lastName),
      email: sanitize(fields.email) || null,
      phone: sanitize(fields.phone) || null,
    };
    // Findable immediately, without reloading ~8k rows. ⚠ A new array, not a
    // push: mutating the prop in place would not re-render the picker, and the
    // contact would be selected but invisible in its own list.
    setLocalContacts((prev) => [created, ...prev]);
    return created;
  };

  /*
   * ⛔⛔ AN IMPORTED ROW'S STAGE IS SHOWN, NOT EDITED — Card P95d.
   *
   * The Stage <select> offers only the seven slugs, so a canonical value like
   * 'Doctor Consult Booked' matched NO option and the browser rendered the
   * first one: the field read "Lead". A rep looking at an imported deal was
   * being shown a stage it does not have, and one keystroke away from trying
   * to save it.
   */
  const stageIsManaged = !!(isEdit && opp && !isUiManagedStage(opp.stage));
  /*
   * ⛔⛔ THE WHOLE RECORD IS READ-ONLY FOR A GHL DEAL — Card P95f item 1.
   *
   * Any save stamps zgx_touched_at and ghlOpportunityUpsert.js then skips the
   * row for ever ("never overwrite a rep-touched record"). One note edit cuts
   * the deal off from GHL permanently — and P95c already refuses UI stage
   * moves on it, so afterwards the stage cannot change by EITHER route while
   * our own message tells the rep to change it in GHL.
   *
   * ⚠ THE DEAL RECORD ONLY. Contact notes, tasks and the timeline are
   * separate and stay fully editable.
   */
  const ghlManaged = !!(isEdit && isGhlManagedRow(opp));

  /**
   * The payload carrying the row's EXISTING stage unchanged.
   *
   * ⛔⛔ IT USED TO OMIT `stage` ENTIRELY — Card P95e, a STOP-SHIP defect.
   * Omitting it skipped the server's guard (which only fires when stage is
   * defined) and left the UPDATE binding its own `|| 'lead'` default, so every
   * edit of an imported deal reset it to 'lead'.
   *
   * ⚠ The server is fixed too, and that fix is the real one — this half just
   * stops the client relying on the server to infer what it meant. Sending the
   * value you want preserved is clearer than sending nothing and hoping.
   */
  const keepStage = (f0) => ({ ...f0, stage: opp ? opp.stage : f0.stage });

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Title is required');
    setSaving(true);
    setSaveError(null);
    try {
      /*
       * ⛔⛔ THE RESULT IS READ — Card P95d, and this is a defect P95c created.
       *
       * P95c taught the server to refuse a stage change on a GHL-imported row.
       * It refuses correctly and writes NOTHING. But this function discarded
       * the result, called onSaved(), closed the modal and reloaded — so a rep
       * who changed the stage AND the value, the owner, a note or the status
       * in one save lost EVERY ONE of those edits, with no message at all. A
       * guard that makes its caller fail silently is worse than no guard: the
       * rep believes the work is saved.
       *
       * ⚠ THE MODAL STAYS OPEN WITH THE FORM INTACT. Closing it would throw
       * away the very edits the refusal was protecting.
       */
      const payload = stageIsManaged ? keepStage(form) : form;
      const r = isEdit
        ? await window.zignagenixAPI.updateCRMOpportunity(opp.id, payload)
        : await window.zignagenixAPI.createCRMOpportunity(payload);

      if (r && r.success === false) {
        setSaveError(r.code === 'STAGE_MANAGED_BY_GHL'
          ? "This deal's stage comes from GHL — change it in GHL. Your other edits were not saved; "
            + 'remove the stage change and save again.'
          : (r.error || 'The save was refused.'));
        return;                       // ⛔ modal stays open, form intact
      }

      /*
       * ⛔ ONLY NOW. onStageWritten ran BEFORE the request, so a refused save
       * still cleared the drawer's overlay for that row — removing a correct
       * overlay on the strength of a write that never happened.
       */
      if (isEdit && onStageWritten) onStageWritten(opp.id);
      onSaved();
    } catch (e) { setSaveError(e && e.message ? e.message : 'The save failed.'); }
    finally { setSaving(false); }
  };

  const f = (key, val) => setForm({ ...form, [key]: val });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640, maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>{ghlManaged ? 'Deal (from GHL)' : isEdit ? 'Edit Opportunity' : 'New Opportunity'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="#6C757D" /></button>
        </div>

        {/* ⛔ ONE LINE, AT THE TOP, SAYING WHAT TO DO INSTEAD — Card P95f item 1.
            The old copy said "change it in GHL" without saying when it would
            come back, which reads as a brush-off rather than an instruction. */}
        {ghlManaged && (
          <div data-ghl-managed-row role="note" style={ghlNoteStyle}>{GHL_MANAGED_SENTENCE}</div>
        )}

        {/* ⚠ `fieldset disabled` makes EVERY control inside read-only in one
            place. Disabling inputs one by one is a list that the next field
            added to this form would quietly fall off. */}
        <fieldset disabled={ghlManaged} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Opportunity Name *</label>
            <input value={form.title} onChange={e => f('title', e.target.value)} style={inp} placeholder="e.g. NK Therapy - John Smith" />
          </div>
          <div>
            <label style={lbl}>Value ($)</label>
            <input type="number" value={form.value} onChange={e => f('value', e.target.value)} style={inp} placeholder="0" />
          </div>
          <div>
            <label style={lbl}>Probability (%)</label>
            <input type="number" min="0" max="100" value={form.probability} onChange={e => f('probability', e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>Status</label>
            <select value={form.status} onChange={e => f('status', e.target.value)} style={inp}>
              {Object.entries(OPP_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Stage</label>
            {stageIsManaged ? (
              <div data-managed-stage style={{ ...inp, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F1F5F9', color: '#475569' }}>
                <span>{opp.stage}</span>
                <span style={{ fontSize: 10, color: '#6C757D' }}>Managed in GHL</span>
              </div>
            ) : (
              <select value={form.stage} onChange={e => f('stage', e.target.value)} style={inp}>
                {DEFAULT_STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
              </select>
            )}
          </div>
          <div>
            <label style={lbl}>Contact</label>
            {/*
              * ⛔⛔ THIS WAS A NATIVE <select> CARRYING EVERY CONTACT — 7,961
              * options in production, ordered by updated_at DESC, no search.
              * Daniel, 22 Oct: "so long it is impossible to use". A native
              * select offers no type-ahead past first-letter jumping, so
              * finding a known person meant scrolling thousands of rows in an
              * order nobody can predict.
              *
              * ⚠ The picker searches the SAME array this select rendered, so
              * it can never show fewer contacts than the select did — it is a
              * strict improvement, not a different data source.
              */}
            {/*
              * ⚠ `disabled` is EXPLICIT, not inherited. The surrounding
              * <fieldset disabled={ghlManaged}> natively disables
              * input/button/select — but this picker's result rows are
              * <div onMouseDown>, which a fieldset does NOT disable. The
              * server refuses a GHL-managed update outright (GHL_MANAGED_ROW),
              * so re-pointing one can only ever be futile, never unsafe;
              * saying it in the component means a future refactor that drops
              * the fieldset cannot silently re-open it.
              */}
            <ContactPicker
              contacts={allContacts}
              value={form.contactId}
              onChange={(id) => f('contactId', id)}
              onCreateContact={createContactInline}
              inputStyle={inp}
              disabled={ghlManaged}
            />
            {/*
              * ⚠ READ-ONLY, AND DELIBERATELY SO. These are the CONTACT's
              * details, shown so staff can see what the deal carries. The
              * deal's own copy (crm_opportunities.contact_email / _phone) is
              * written by the server from the linked contact — see the create
              * handler at routes/crm.js:12586 — not typed here.
              */}
            {selectedContact && (
              <div style={{ fontSize: 11, color: '#6C757D', marginTop: 4 }}>
                <div>Email: {selectedContact.email || '—'}</div>
                <div>Phone: {selectedContact.phone || '—'}</div>
              </div>
            )}
          </div>
          <div>
            <label style={lbl}>Owner</label>
            <select value={form.ownerId} onChange={e => f('ownerId', e.target.value)} style={inp}>
              <option value="">Unassigned</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Pipeline</label>
            <select value={form.pipelineId} onChange={e => f('pipelineId', e.target.value)} style={inp}>
              <option value="">Default</option>
              {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Expected Close Date</label>
            <input type="date" value={form.expectedCloseDate} onChange={e => f('expectedCloseDate', e.target.value)} style={inp} />
          </div>

          {/* ZignaGenix Custom Fields */}
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #E5E7EB', paddingTop: 12, marginTop: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#2E8B98' }}>ZignaGenix Custom Fields</span>
          </div>
          <div>
            <label style={lbl}>Treatment Type</label>
            <select value={form.treatmentType} onChange={e => f('treatmentType', e.target.value)} style={inp}>
              <option value="">Select...</option>
              {TREATMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Budget</label>
            <input type="number" value={form.budget} onChange={e => f('budget', e.target.value)} style={inp} placeholder="0" />
          </div>
          <div>
            <label style={lbl}>Qualification Score</label>
            <input type="number" value={form.qualificationScore} onChange={e => f('qualificationScore', e.target.value)} style={inp} placeholder="0-100" />
          </div>
          <div>
            <label style={lbl}>Lead Source</label>
            <input value={form.leadSourceType} onChange={e => f('leadSourceType', e.target.value)} style={inp} placeholder="e.g. Physician Referral" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Medical Condition</label>
            <input value={form.medicalCondition} onChange={e => f('medicalCondition', e.target.value)} style={inp} placeholder="e.g. Metastatic breast cancer" />
          </div>
          <div>
            <label style={lbl}>Tags (comma-separated)</label>
            <input value={form.tags} onChange={e => f('tags', e.target.value)} style={inp} placeholder="vip, high-value" />
          </div>
          <div>
            <label style={lbl}>Notes</label>
            <input value={form.notes} onChange={e => f('notes', e.target.value)} style={inp} placeholder="Additional notes..." />
          </div>
          {form.status === 'lost' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Lost Reason</label>
              <input value={form.lostReason} onChange={e => f('lostReason', e.target.value)} style={inp} placeholder="Why was this deal lost?" />
            </div>
          )}
        </div>

        {/* ⛔ A REFUSAL IS SHOWN HERE, beside the button that caused it, and the
            form stays exactly as the rep left it. An alert() would be
            dismissed and gone; this stays until they act on it. */}
        </fieldset>

        {saveError && (
          <div role="alert" style={saveErrStyle}>{saveError}</div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSm}>{ghlManaged ? 'Close' : 'Cancel'}</button>
          {/* ⛔ NO SAVE BUTTON ON A GHL DEAL — an affordance that refuses is a
              promise the screen cannot keep, and here it would also be a
              one-way door: the save that failed would still have frozen the
              row against GHL. */}
          {!ghlManaged && (
            <button onClick={handleSave} disabled={saving} style={btnPrimary}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create Deal'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4 };

// ─── Opportunity Detail Modal ────────────────────────────────────────────────

function OppDetailModal({ opp, onClose, onEdit }) {
  const fmt = (v) => v != null && v !== '' ? `$${Number(v).toLocaleString()}` : '—';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const fields = [
    ['Status', <Badge text={OPP_STATUSES[opp.status]?.label || opp.status} color={OPP_STATUSES[opp.status]?.color} bg={OPP_STATUSES[opp.status]?.bg} />],
    ['Stage', <Badge text={STAGE_LABELS[opp.stage] || opp.stage} color={STAGE_COLORS[opp.stage] || '#6C757D'} />],
    ['Value', fmt(opp.value)],
    ['Probability', opp.probability ? `${opp.probability}%` : '—'],
    ['Owner', opp.owner_name || '—'],
    ['Contact', opp.contact_name || '—'],
    ['Email', opp.contact_email || '—'],
    ['Phone', opp.contact_phone || '—'],
    ['Treatment Type', opp.treatment_type || '—'],
    ['Budget', fmt(opp.budget)],
    ['Qualification', opp.qualification_score || '—'],
    ['Lead Source', opp.lead_source_type || '—'],
    ['Medical Condition', opp.medical_condition || '—'],
    ['Tags', opp.tags || '—'],
    ['Created', fmtDate(opp.created_at)],
    ['Updated', fmtDate(opp.updated_at)],
  ];
  if (opp.lost_reason) fields.push(['Lost Reason', opp.lost_reason]);
  // ⚠ CONDITIONAL, reusing the lost_reason idiom directly above. This row used
  // to render unconditionally as `Expected Close  —`, which reads as "no date
  // set" when the truth is "we can never have one": GHL does not return
  // expectedCloseDate on /opportunities/search and 0 of 7,803 rows carry a
  // date. Rendering nothing makes no claim, which is the MoneyWithCoverage
  // rule applied to a date.
  // ⚠⚠ It is NOT deleted, because the manual write path survives — there is a
  // real <input type="date"> below and the create/update handlers still write
  // the column. A date a human enters WILL appear here. Deleting this row
  // would have made that input silently unobservable.
  if (opp.expected_close_date) fields.push(['Expected Close', fmtDate(opp.expected_close_date)]);

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 520 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>{opp.title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="#6C757D" /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {fields.map(([label, val], i) => (
            <div key={i} style={{ padding: '6px 0' }}>
              <div style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 13, color: '#212529' }}>{val}</div>
            </div>
          ))}
        </div>

        {opp.notes && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#F9FAFB', borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
            <div style={{ fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap' }}>{opp.notes}</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSm}>Close</button>
          <button onClick={onEdit} style={btnPrimary}><Edit2 size={13} /> Edit</button>
        </div>
      </div>
    </div>
  );
}

// ─── §2 Multi-Select (P4a) — compact checkbox dropdown, plain CSS ────────────
function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #DEE2E6',
                 background: selected.length ? '#E7F4F6' : '#fff', color: selected.length ? '#2E8B98' : '#495057', cursor: 'pointer' }}>
        {label}{selected.length ? ` (${selected.length})` : ''} ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 50, background: '#fff',
                      border: '1px solid #DEE2E6', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      padding: 8, minWidth: 180, maxHeight: 240, overflowY: 'auto' }}>
          {options.length === 0 && <div style={{ fontSize: 12, color: '#ADB5BD', padding: 4 }}>No options</div>}
          {options.map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                                            padding: '3px 4px', cursor: 'pointer', color: '#495057' }}>
              <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
              {opt.label}
            </label>
          ))}
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])}
              style={{ fontSize: 11, marginTop: 6, padding: '3px 8px', borderRadius: 6, border: '1px solid #DEE2E6',
                       background: '#F8F9FA', color: '#6C757D', cursor: 'pointer', width: '100%' }}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── §3 Calendar view (P4b) — month grid over the pipeline ──────────────────
// Counts come from crm:opportunities:calendar:month (no PHI); clicking a day
// fetches the agenda from crm:opportunities:calendar:day (PHI-logged
// server-side per linked patient).
const OPP_CAL_KINDS = [
  { key: 'consultations', label: 'Consults', color: '#2E8B98' },
  { key: 'therapy', label: 'Therapy', color: '#F26522' },
  { key: 'followups', label: 'Follow-ups', color: '#6C757D' },
];

function OppCalendarView({ onContactClick }) {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [days, setDays] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [loading, setLoading] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    window.zignagenixAPI.getOppCalendarMonth(ym.y, ym.m, tz)
      .then(r => { if (alive && r && !r.error) setDays(r.days || {}); })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    setSelectedDay(null); setAgenda(null);
    return () => { alive = false; };
  }, [ym.y, ym.m, tz]);

  const openDay = async (dateISO) => {
    setSelectedDay(dateISO);
    setAgenda(null);
    try {
      const r = await window.zignagenixAPI.getOppCalendarDay(dateISO, tz);
      if (r && !r.error) setAgenda(r.items || []);
    } catch { setAgenda([]); }
  };

  const nav = (delta) => setYm(({ y, m }) => {
    const d = new Date(y, m - 1 + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  });

  const first = new Date(ym.y, ym.m - 1, 1);
  const startPad = (first.getDay() + 6) % 7; // Monday-first grid
  const daysInMonth = new Date(ym.y, ym.m, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${ym.y}-${String(ym.m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);

  const monthLabel = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <button onClick={() => nav(-1)} style={{ border: '1px solid #DEE2E6', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>‹</button>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#212529', minWidth: 150, textAlign: 'center' }}>{monthLabel}</div>
        <button onClick={() => nav(1)} style={{ border: '1px solid #DEE2E6', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>›</button>
        {loading && <span style={{ fontSize: 12, color: '#ADB5BD' }}>Loading…</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          {OPP_CAL_KINDS.map(k => (
            <span key={k.key} style={{ fontSize: 11, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: k.color, display: 'inline-block' }} />{k.label}
            </span>
          ))}
        </div>
      </div>

      <div className="responsive-scroll" style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, minWidth: 640 }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} style={{ fontSize: 11, fontWeight: 600, color: '#ADB5BD', textAlign: 'center', padding: 4 }}>{d}</div>
          ))}
          {cells.map((dateISO, i) => {
            if (!dateISO) return <div key={`pad${i}`} />;
            const counts = days[dateISO];
            const isSel = selectedDay === dateISO;
            const isToday = dateISO === todayISO;
            return (
              <button key={dateISO} onClick={() => openDay(dateISO)}
                style={{ minHeight: 64, textAlign: 'left', padding: 6, borderRadius: 8, cursor: 'pointer',
                         border: isSel ? '2px solid #2E8B98' : '1px solid #E9ECEF',
                         background: isToday ? '#F0FAF9' : '#fff' }}>
                <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? '#2E8B98' : '#6C757D' }}>
                  {Number(dateISO.slice(8))}
                </div>
                {counts && (
                  <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 3 }}>
                    {OPP_CAL_KINDS.map(k => counts[k.key] > 0 && (
                      <span key={k.key} style={{ fontSize: 10, fontWeight: 600, color: '#fff', background: k.color,
                                                 borderRadius: 8, padding: '0 6px' }}>{counts[k.key]}</span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div style={{ marginTop: 14, borderTop: '1px solid #E9ECEF', paddingTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#212529', marginBottom: 8 }}>
            {new Date(`${selectedDay}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
          {agenda === null ? (
            <div style={{ fontSize: 12, color: '#ADB5BD' }}>Loading agenda…</div>
          ) : agenda.length === 0 ? (
            <div style={{ fontSize: 12, color: '#ADB5BD' }}>No pipeline events this day.</div>
          ) : (
            agenda.map(it => {
              const kindDef = OPP_CAL_KINDS.find(k => (k.key === 'consultations' ? 'consultation' : k.key === 'therapy' ? 'therapy' : 'followup') === it.kind) || OPP_CAL_KINDS[2];
              return (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', borderBottom: '1px solid #F1F3F5', fontSize: 12.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: kindDef.color, flexShrink: 0 }} />
                  <span style={{ color: '#ADB5BD', minWidth: 52 }}>
                    {it.time ? new Date(it.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—'}
                  </span>
                  <span style={{ fontWeight: 600, color: '#212529' }}>{it.title}</span>
                  {it.contactName && (
                    onContactClick && it.contactId
                      ? <span onClick={(e) => onContactClick(it.contactId, e)} style={{ color: '#2E8B98', cursor: 'pointer', textDecoration: 'underline' }}>{it.contactName}</span>
                      : <span style={{ color: '#6C757D' }}>{it.contactName}</span>
                  )}
                  {it.opportunityTitle && <span style={{ fontSize: 11, color: '#ADB5BD' }}>· {it.opportunityTitle} ({STAGE_LABELS[it.stage] || it.stage})</span>}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
